import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
    AccountProfileResponse,
    RequestEmailChangeData,
    RequestEmailChangeResponse,
    ConfirmEmailChangeResponse,
    VerifyEmailChangeResponse,
    CancelEmailChangeResponse,
    RequestPasswordChangeResponse,
} from '../types';

export function useAccountProfile() {
    return useQuery({
        queryKey: ['accountProfile'],
        queryFn: () => api.get<AccountProfileResponse>('/api/account/profile'),
    });
}

export function useRequestEmailChange() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: RequestEmailChangeData) =>
            api.post<RequestEmailChangeResponse>('/api/account/profile/email/request', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountProfile'] });
        },
    });
}

export function useConfirmEmailChange() {
    return useMutation({
        mutationFn: (token: string) =>
            api.get<ConfirmEmailChangeResponse>(
                `/api/account/profile/email/confirm?token=${encodeURIComponent(token)}`
            ),
    });
}

export function useVerifyEmailChange() {
    return useMutation({
        mutationFn: (token: string) =>
            api.get<VerifyEmailChangeResponse>(
                `/api/account/profile/email/verify?token=${encodeURIComponent(token)}`
            ),
    });
}

export function useCancelEmailChange() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.delete<CancelEmailChangeResponse>('/api/account/profile/email/pending'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountProfile'] });
        },
    });
}

export function useRequestPasswordChange() {
    return useMutation({
        mutationFn: () => api.post<RequestPasswordChangeResponse>('/api/account/profile/password/request'),
    });
}

export function useRefreshAccountProfile() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({ queryKey: ['accountProfile'] });
    };
}
